import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import signInFormValidation from 'pages/sign-in/validation/sign-in-form-validation';
import './index.scss';

import {
  Label, Title,
} from 'components';
import useAuthorization from 'base/auth/hooks/use-authorization';
import { BaseLayout } from 'components/templates';
import { authSliceActions } from 'base/auth/slice';
import { selectCallbackPath, selectFromMfaSetup } from 'base/auth/selectors';
import useAuthentication from 'base/auth/hooks/use-authentication';
import {
  selectMfaConfigs,
  selectMfaConfigStatus,
  selectSelectedMfaType,
  selectShowMfaSelection,
} from 'base/mfa/selectors';
import { mfaSliceActions } from 'base/mfa/slice';
import SignInForm from '../sign-in-form';
import MfaSelectionView from '../mfa-selection';
import MfaVerificationView from '../mfa-verification';
import MfaSelectConfigEmail from '../mfa-request-code';

const SignInView = () => {
  useAuthorization();
  const methods = useForm({
    defaultValues: {
      name: '',
      password: '',
      retainLogin: false,
    },
    resolver: yupResolver(signInFormValidation),
    shouldFocusError: true,
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState<'signin' | 'mfa-selection' | 'mfa-verification' | 'request-mfa-email'>('signin');
  const [selectedMfaType, setSelectedMfaType] = useState<'EMAIL' | 'TOTP' | null>(null);
  const callbackPath = useSelector(selectCallbackPath);
  const fromMfaSetup = useSelector(selectFromMfaSetup);
  const { isAuthenticated } = useAuthentication();
  const showMfaSelection = useSelector(selectShowMfaSelection);
  const mfaConfigStatus = useSelector(selectMfaConfigStatus);
  const mfaConfigs = useSelector(selectMfaConfigs);
  const reduxSelectedMfaType = useSelector(selectSelectedMfaType);

  const handleSubmitFrom: SubmitHandler<any> = useCallback(
    (data) => {
      dispatch(authSliceActions.signIn(data));
    },
    [dispatch],
  );

  const handleMfaTypeSelected = useCallback((mfaType: 'EMAIL' | 'TOTP') => {
    setSelectedMfaType(mfaType);
    setCurrentView('mfa-verification');
  }, []);

  const handleBackToSignIn = useCallback(() => {
    setCurrentView('signin');
    setSelectedMfaType(null);
    dispatch(mfaSliceActions.resetMfaState());
  }, [dispatch]);

  const handleBackToMfaSelection = useCallback(() => {
    setCurrentView('mfa-selection');
    setSelectedMfaType(null);
    // Reset challenge state while preserving original credentials
    dispatch(mfaSliceActions.resetMfaChallengeState());
  }, [dispatch]);

  React.useEffect(() => {
    if (isAuthenticated()) {
      if (callbackPath) {
        navigate(`/${callbackPath}`);
      } else {
        navigate('../');
      }
    }
  }, [callbackPath, isAuthenticated]);

  React.useEffect(() => {
    dispatch(authSliceActions.setCallbackPath(callbackPath || '/'));
    dispatch(mfaSliceActions.clearStaleAuthState());

    return () => {
      dispatch(mfaSliceActions.resetMfaState());
    };
  }, [callbackPath, dispatch]);

  React.useEffect(() => {
    if (mfaConfigStatus === 'REQUEST_CONFIG_EMAIL') {
      setCurrentView('request-mfa-email');
      setSelectedMfaType(null);
    }
    if (showMfaSelection && mfaConfigs.length > 0) {
      setCurrentView('mfa-selection');
      setSelectedMfaType(null);
    }
  }, [showMfaSelection, mfaConfigs, mfaConfigStatus]);

  React.useEffect(() => {
    if (reduxSelectedMfaType && currentView === 'mfa-selection') {
      setSelectedMfaType(reduxSelectedMfaType);
    }
  }, [reduxSelectedMfaType, currentView]);

  const getPageTitle = () => {
    switch (currentView) {
      case 'signin':
        return 'Welcome back!';
      case 'mfa-selection':
        return 'Multi-Factor Authentication';
      case 'mfa-verification':
        return selectedMfaType === 'EMAIL' ? 'Email Verification' : 'Authenticator App';
      case 'request-mfa-email':
        return 'Setup Multi-Factor Authentication';
      default:
        return 'Welcome back!';
    }
  };

  const getPageDescription = () => {
    switch (currentView) {
      case 'signin':
        return fromMfaSetup
          ? 'Thank you for successfully configuring multi-factor authentication. Please sign in to continue.'
          : 'Sign in to your account to continue';
      case 'mfa-selection':
        return 'Please select your preferred authentication method to continue';
      case 'mfa-verification':
        return selectedMfaType === 'EMAIL'
          ? 'Enter the code sent to your email to continue.'
          : 'Open your authenticator app and enter the 6-digit code.';
      case 'request-mfa-email':
        return 'To complete your sign-in, you must configure Multi-Factor Authentication (MFA).';
      default:
        return 'Sign in to your account to continue';
    }
  };

  return (
    <BaseLayout>
      <>
        <div style={{ textAlign: 'center' }}>
          <Title>{getPageTitle()}</Title>
        </div>
        <div style={{ textAlign: 'center' }}>
          <Label>{getPageDescription()}</Label>
        </div>
        <br />
        <FormProvider {...methods}>
          {currentView === 'signin' && (
            <SignInForm onSubmit={handleSubmitFrom} />
          )}

          {currentView === 'mfa-selection' && (
            <MfaSelectionView
              onMfaTypeSelected={handleMfaTypeSelected}
              onBackToSignIn={handleBackToSignIn}
            />
          )}

          {currentView === 'request-mfa-email' && (
            <MfaSelectConfigEmail
              onBackToSignIn={handleBackToSignIn}
            />
          )}
          {currentView === 'mfa-verification' && selectedMfaType && (
            <MfaVerificationView
              mfaType={selectedMfaType}
              onBack={handleBackToMfaSelection}
              onCancel={handleBackToSignIn}
            />
          )}
        </FormProvider>
      </>

    </BaseLayout>

  );
};

export default SignInView;
